import { Album, SyncMetadata } from '../types/album';

/**
 * Service for intelligent album updates that preserve custom data
 * and track changes to avoid unnecessary sync operations
 */

export interface UpdateOptions {
  forceUpdate?: boolean; // Force update even if no changes detected
  preserveCustomFields?: boolean; // Preserve manually edited fields (default: true)
  updateImages?: boolean; // Update cover images (default: only if missing)
  updateTracklist?: boolean; // Update tracklist (default: true)
  syncSource?: 'discogs' | 'musicbrainz' | 'manual';
}

export interface AlbumChanges {
  hasChanges: boolean;
  changedFields: string[];
  preservedCustomFields: string[];
  newTracklist: boolean;
  imageNeedsUpdate: boolean;
}

class AlbumUpdateService {
  
  /**
   * Intelligently merge new album data with existing album, preserving custom changes
   */
  mergeAlbumData(
    existingAlbum: Album, 
    newData: Partial<Album>, 
    options: UpdateOptions = {}
  ): { album: Album; changes: AlbumChanges } {
    const opts = {
      preserveCustomFields: true,
      updateImages: false, // Only update if missing by default
      updateTracklist: true,
      syncSource: 'discogs' as const,
      ...options
    };

    const changes: AlbumChanges = {
      hasChanges: false,
      changedFields: [],
      preservedCustomFields: [],
      newTracklist: false,
      imageNeedsUpdate: false
    };

    // Start with existing album
    const mergedAlbum: Album = { ...existingAlbum };

    // Get existing custom fields from sync metadata
    const customFields = existingAlbum.syncMetadata?.customFields || {};

    // Fields to potentially update from new data
    const updatableFields = [
      'artist', 'artists', 'title', 'year', 'releaseYear', 
      'genres', 'isVariousArtists', 'format'
    ];

    // Check each field for changes
    for (const field of updatableFields) {
      if (newData[field as keyof Album] !== undefined) {
        const existingValue = existingAlbum[field as keyof Album];
        const newValue = newData[field as keyof Album];

        // Skip update if this field has been manually customized and we're preserving custom fields
        if (opts.preserveCustomFields && customFields[field]) {
          changes.preservedCustomFields.push(field);
          continue;
        }

        // Check if value has actually changed
        if (this.hasFieldChanged(existingValue, newValue)) {
          (mergedAlbum as any)[field] = newValue;
          changes.changedFields.push(field);
          changes.hasChanges = true;
        }
      }
    }

    // Handle tracklist separately
    if (opts.updateTracklist && newData.tracklist) {
      if (!existingAlbum.tracklist || existingAlbum.tracklist.length === 0) {
        mergedAlbum.tracklist = newData.tracklist;
        changes.newTracklist = true;
        changes.hasChanges = true;
        changes.changedFields.push('tracklist');
      } else if (!customFields.tracklist) {
        // Update existing tracklist if not customized
        if (this.hasTracklistChanged(existingAlbum.tracklist, newData.tracklist)) {
          mergedAlbum.tracklist = newData.tracklist;
          changes.changedFields.push('tracklist');
          changes.hasChanges = true;
        }
      } else {
        changes.preservedCustomFields.push('tracklist');
      }
    }

    // Handle cover image
    if (opts.updateImages || !existingAlbum.coverImageBlob || existingAlbum.coverImageBlob.size === 0) {
      if (newData.coverImageUrl && newData.coverImageUrl !== existingAlbum.coverImageUrl) {
        changes.imageNeedsUpdate = true;
        changes.hasChanges = true;
        mergedAlbum.coverImageUrl = newData.coverImageUrl;
      }
    }

    // Update sync metadata
    const updatedSyncMetadata: SyncMetadata = {
      ...existingAlbum.syncMetadata,
      lastSyncTime: new Date().toISOString(),
      customFields: { ...customFields },
      syncSources: {
        ...existingAlbum.syncMetadata?.syncSources,
        [opts.syncSource]: true
      }
    };

    // Set source-specific sync time
    if (opts.syncSource === 'discogs') {
      updatedSyncMetadata.lastDiscogsSyncTime = new Date().toISOString();
      if (newData.syncMetadata?.discogsResourceVersion) {
        updatedSyncMetadata.discogsResourceVersion = newData.syncMetadata.discogsResourceVersion;
      }
    } else if (opts.syncSource === 'musicbrainz') {
      updatedSyncMetadata.lastMusicBrainzSyncTime = new Date().toISOString();
    }

    mergedAlbum.syncMetadata = updatedSyncMetadata;

    return { album: mergedAlbum, changes };
  }

  /**
   * Mark fields as manually customized to prevent automatic overwriting
   */
  markFieldsAsCustomized(album: Album, fieldNames: string[]): Album {
    const syncMetadata = album.syncMetadata || this.createDefaultSyncMetadata();
    
    for (const field of fieldNames) {
      syncMetadata.customFields[field] = true;
    }

    syncMetadata.syncSources.manual = true;
    syncMetadata.lastSyncTime = new Date().toISOString();

    return {
      ...album,
      syncMetadata
    };
  }

  /**
   * Check if sync is needed based on time and resource version
   */
  needsSync(album: Album, syncSource: 'discogs' | 'musicbrainz' = 'discogs'): boolean {
    if (!album.syncMetadata) return true;

    const now = new Date();
    const lastSync = syncSource === 'discogs' 
      ? album.syncMetadata.lastDiscogsSyncTime 
      : album.syncMetadata.lastMusicBrainzSyncTime;

    if (!lastSync) return true;

    const lastSyncDate = new Date(lastSync);
    const daysSinceSync = (now.getTime() - lastSyncDate.getTime()) / (1000 * 60 * 60 * 24);

    // Sync if more than 7 days old (configurable)
    return daysSinceSync > 7;
  }

  /**
   * Get summary of what would be updated
   */
  previewUpdate(existingAlbum: Album, newData: Partial<Album>, options: UpdateOptions = {}): AlbumChanges {
    const { changes } = this.mergeAlbumData(existingAlbum, newData, options);
    return changes;
  }

  private hasFieldChanged(existing: any, newValue: any): boolean {
    if (Array.isArray(existing) && Array.isArray(newValue)) {
      return JSON.stringify(existing.sort()) !== JSON.stringify(newValue.sort());
    }
    
    if (typeof existing === 'object' && typeof newValue === 'object' && existing && newValue) {
      return JSON.stringify(existing) !== JSON.stringify(newValue);
    }

    return existing !== newValue;
  }

  private hasTracklistChanged(existing: any[], newTracklist: any[]): boolean {
    if (existing.length !== newTracklist.length) return true;
    
    for (let i = 0; i < existing.length; i++) {
      if (existing[i].position !== newTracklist[i].position ||
          existing[i].title !== newTracklist[i].title ||
          existing[i].duration !== newTracklist[i].duration) {
        return true;
      }
    }
    
    return false;
  }

  private createDefaultSyncMetadata(): SyncMetadata {
    return {
      lastSyncTime: new Date().toISOString(),
      customFields: {},
      syncSources: {}
    };
  }
}

export const albumUpdateService = new AlbumUpdateService();